import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { VesselAnnouncementService } from '../../core/api/vessel-announcement.service';
import { VesselAnnouncement } from '../../shared/models/vessel-announcement';
import { statusTone } from '../../shared/status-tone';
import { AppNavComponent } from '../../shared/app-nav.component';
import { BackButtonComponent } from '../../shared/back-button.component';
import { TranslatePipe } from '../../shared/translate.pipe';
import { StatusLabelPipe } from '../../shared/status-label.pipe';
import { MneDatePipe } from '../../shared/mne-date.pipe';

@Component({
  standalone: true,
  imports: [RouterLink, AppNavComponent, BackButtonComponent, TranslatePipe, StatusLabelPipe, MneDatePipe],
  template: `
        <app-nav></app-nav>
    <main class="page-shell">
      <header class="topbar">
        <app-back></app-back>
        <h1>{{ 'va.detailTitle' | t }}: #{{ item()?.Id }}</h1>
      </header>
      @if (item(); as a) {
        <section class="detail-card">
          <div class="detail-heading">
            <div>
              <p class="eyebrow">{{ 'common.id' | t }}: {{ a.Id }}</p>
              <h2>{{ a.VesselName || ('va.fallback' | t) }}</h2>
            </div>
            <span class="status-pill" [class]="tone(a.StatusName)">{{ (a.StatusName | statusLabel) || ('status.draft' | t) }}</span>
          </div>
          <dl>
            <dt>{{ 'va.lubarisNumber' | t }}</dt><dd>{{ a.LubarisNumber ?? '—' }}</dd>
            <dt>{{ 'va.organizationUnit' | t }}</dt><dd>{{ a.OrganizationUnitName || '—' }}</dd>
            <dt>{{ 'va.commercialEta' | t }}</dt><dd>{{ a.LubarisCommercialEtaConfirmation | mneDate }}</dd>
            <dt>{{ 'va.operationEta' | t }}</dt><dd>{{ a.LubarisOperationEtaConfirmation | mneDate }}</dd>
            <dt>{{ 'va.fromCity' | t }}</dt><dd>{{ a.FromCity || '—' }}</dd>
            <dt>{{ 'va.toCity' | t }}</dt><dd>{{ a.ToCity || '—' }}</dd>
            <dt>{{ 'common.createdBy' | t }}</dt><dd>{{ a.CreatedByName || '—' }}</dd>
            <dt>{{ 'va.recipient' | t }}</dt><dd>{{ a.RecipientCode || '—' }}</dd>
          </dl>

          @if (a.Rows?.length) {
            <h3 class="section-title">{{ 'wo.rows' | t }}</h3>
            <section class="list">
              @for (row of a.Rows; track row.Id) {
                <div class="list-row card-style static tone-amber">
                  <div class="card-style-header">
                    <span class="row-id">{{ 'va.rowNo' | t }} {{ row.Id }}</span>
                  </div>
                  <dl class="card-fields">
                    <div class="field-row"><dt>{{ 'wo.packagingType' | t }}</dt><dd>{{ row.PackagingTypeName || '—' }}</dd></div>
                    <div class="field-row"><dt>{{ 'wo.cargoType' | t }}</dt><dd>{{ row.CargoTypeName || '—' }}</dd></div>
                    <div class="field-row"><dt>{{ 'wo.packagingQuantity' | t }}</dt><dd>{{ row.PackagingQuantity ?? '—' }}</dd></div>
                  </dl>
                </div>
              }
            </section>
          }
        </section>
        @if (error()) { <p class="error notice">{{ error() | t }}</p> }
      } @else if (error()) { <p class="error notice">{{ error() | t }}</p> } @else { <div class="skeleton-detail">
        <div class="skeleton-heading"></div>
        <div class="skeleton-line w-70"></div>
        <div class="skeleton-line w-55"></div>
        <div class="skeleton-line w-85"></div>
        <div class="skeleton-line w-40"></div>
      </div> }
    </main>
  `
})
export class VesselAnnouncementDetailPage implements OnInit {
  readonly item = signal<VesselAnnouncement | null>(null);
  readonly error = signal('');

  constructor(private readonly route: ActivatedRoute, private readonly api: VesselAnnouncementService) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    if (!id) { this.error.set('va.missingId'); return; }
    this.api.get(id).subscribe({
      next: item => this.item.set(item),
      error: err => this.error.set(err?.error?.Message ?? err?.error?.message ?? 'va.detailLoadError')
    });
  }
  tone(status?: string | null): string { return statusTone(status); }
}
